<template>
  <div
    v-disableable="{disabled: $store.getters['session/onTour']}"
    class="breadcrumbs-component-container"
    :class="{ v2: version === 'v2' }"
  >
    <template v-if="dashboardCrumbs && dashboardCrumbs.length > 0">
      <template
        v-for="(dashboardCrumb, index) in dashboardCrumbs"
      >
        <icon
          v-if="index > 0"
          :key="`dashboard-icon-${index}`"
          name="arrow"
          variant="c5d0de"
        />
        <router-link
          :key="dashboardCrumb.id"
          :to="dashboardCrumb.route"
          :title="dashboardCrumb.name"
          v-html="dashboardCrumb.name"
        />
      </template>
    </template>
    <router-link
      v-else
      :to="{name: 'home'}"
      aria-label="Home"
    >
      <icon
        name="home"
        :variant="homeIconVariant"
        class="home-icon"
      />
    </router-link>
    <template v-for="(crumb, index) in breadcrumbList">
      <icon
        :key="`icon-${index}`"
        name="arrow"
        variant="c5d0de"
      />
      <div
        v-if="(index + 1) === breadcrumbList.length"
        :key="`div-${index}`"
        class="last"
        v-html="crumb.name"
      />
      <router-link
        v-else
        :key="`link-${index}`"
        :to="crumb.route"
        :title="crumb.name"
        v-html="crumb.name"
      />
    </template>
    <router-link
      :to="backItem.route"
      class="back"
    >
      <icon
        name="arrow"
        :variant="iconVariant"
      />
      <span>Back To {{ backItem.name }}</span>
    </router-link>
    <slot name="right" />
    <div class="spacer" />
    <slot name="floatRight" />
  </div>
</template>

<script>
import GlobalMixin from '@/mixins/global';
import Icon from '@components/singletons/icon';

export default {
  components: { Icon },
  mixins: [GlobalMixin],
  props: {
    breadcrumbs: {
      type: Array,
      default: () => [],
    },
    version: {
      type: String,
      default: 'v1',
    },
  },
  computed: {
    // this should something that isn't the current crumb
    backItem() {
      if (this.breadcrumbs.length > 1) {
        // If more than 1 breadcrumb just return the second to last (last one is current crumb)
        return this.breadcrumbs.slice(-2).shift();;
      } else if (this.breadcrumbs.length === 1 && this.currentResources.length === 1) {
        // Only one crumb and one resource, we need the resource
        let resource = this.currentResources.slice(0).pop();
        return this.resourceCrumb(resource);
      } else if (this.currentResources.length > 1) {
        // if there's more than one resource, return the second to last resource
        let resource = this.currentResources.slice(-2).shift();
        return this.resourceCrumb(resource);
      } else if (this.dashboardCrumbs?.length > 0) {
        return this.dashboardCrumbs[this.dashboardCrumbs.length - 1];
      } else {
        return {
          name: 'Home',
          route: {name: 'home'},
        };
      }
    },
    breadcrumbList() {
      let resources = this.currentResources.map(resource => {
        return this.resourceCrumb(resource);
      });

      return [...resources, ...this.breadcrumbs];
    },
    currentResources() {
      if (this.$route && this.$route.query && this.$route.query.nav) {
        const ids = this.$route.query.nav.split(',').map(q => q.split('-')[0]);
        // filter is needed for dynamic nav item edge cases
        return ids.map(id => this.$store.getters['navigationItems/navigationItems'][id]).filter(r => r);
      } else {
        return [];
      }
    },
    homeIconVariant() {
      if (this.version === 'v2') return '155EEF';
      if (this.$store.getters['app/isMasteryApp']) return 'mastery';
      return '4fb9de';
    },
    iconVariant() {
      return this.$store.getters['app/isMasteryApp'] ? 'mastery' : '4fb9de';
    },
    dashboardCrumbs() {
      // if there's a nav query param, find that nav item in menu items if it exists
      const menuItems = this.$store.getters['navigationItems/menuItems'];
      if( !menuItems ) { return; };
      const queryItem = menuItems.find(ni => ni.id === this.currentResource.id);
      if (queryItem) return;

      // then see if there's a dashboard item we last visited
      const lastDashboardIdsArray = this.$store.getters['dashboards/lastVisitedId'];

      let lastVisitedCrumbs = [];

      if (lastDashboardIdsArray?.length > 0) {
        if (lastDashboardIdsArray?.length === 1) {
          const item = menuItems.find(ni => {
            return ni.destination === 'dashboard' && lastDashboardIdsArray.includes(ni.resourceId);
          });
          if (item) {
            lastVisitedCrumbs.push(item);
          }
        } else {
          const dashboardGroupMenuItem = menuItems.find(ni => {
            return ni.destination === 'dashboard_group' && lastDashboardIdsArray[0] === ni.resourceId;
          });

          if (dashboardGroupMenuItem) {
            lastVisitedCrumbs.push(dashboardGroupMenuItem);
            const dashboardGroupNavGroup = this.$store.getters['navigationGroups/navigationGroups'][dashboardGroupMenuItem.resourceId];
            const dashboardGroupNavGroupNavItems = dashboardGroupNavGroup?.navigationItems(this.$store);

            const dashboardGroupDashboardNavigationItem = dashboardGroupNavGroupNavItems?.find(ni => {
              return ni.destination === 'dashboard' && lastDashboardIdsArray[1] === ni.resourceId;
            });
            if (dashboardGroupDashboardNavigationItem) {
              lastVisitedCrumbs.push({
                route: {
                  name: 'dashboard#group',
                  params: {
                    navigationGroupId: dashboardGroupNavGroup.id,
                    dashboardId: dashboardGroupDashboardNavigationItem.resourceId,
                  },
                  query: this.currentQuery,
                },
                name: dashboardGroupDashboardNavigationItem.name,
              });
            }
          }
        }
      }
      return lastVisitedCrumbs;
      // otherwise assume home should be the last base crumb
    },
  },
  methods: {
    resourceCrumb(resource) {
      const route = {
        ...resource,
        route: {
          ...resource.route,
          query: this.resourceQuery(resource),
        },
      };
      // This is a hack for the library to keep the tagging query context in the breadcrumbs
      if (this.$route?.query?.tagging) {
        route.route.query.tagging = this.$route.query.tagging;
      }
      return route;
    },
    resourceQuery(resource) {
      // Find index of resource in query string (comma separated resources)
      const index = this.currentResources.findIndex(r => r.query === resource.query);
      // Trim resources up to the resource and cast back into query string
      return { nav: this.currentResources.slice(0, index + 1).map(r => r.query).join(',') };
    },
  },
};
</script>
<style lang='scss' scoped>
.breadcrumbs-component-container {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  height: rem-calc(45);
  padding: 0 rem-calc(48);
  width: 100%;

  &.v2 {
    padding: 0 rem-calc(32);
    background: white;
    border-bottom: 1px solid #E3E8EF;

    > .last, > a, > img {
      font-size: rem-calc(12);
      color: #4B5565;
    }

    a:has(> img) {
      margin-bottom: -3px;
    }
  }

  > .last, > a, > img {
    color: #7f8fa4;
    font-size: rem-calc(11);
    line-height: 1.5;
    padding-right: rem-calc(12);
    text-align: center;

    &::after {
      display: block;
      content: attr(title);
      font-weight: $semi-bold;
      height: 0;
      overflow: hidden;
      visibility: hidden;
    }
  }

  > .last {
    display: inline-block;
  }

  > a:first-of-type {
    flex-shrink: 0;
    padding-right: rem-calc(14);
  }

  > .last, > a:hover {
    font-weight: $semi-bold;
  }

  > a:not(.back), > img, > .last {
    @include breakpoint(small) {
      display: none;
    }
  }

   > a:not(.back), .last {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .back {
    align-items: center;
    display: none;
    font-weight: $semi-bold;
    overflow: hidden;

    @include breakpoint(small) {
      display: flex;
    }

    span {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  /deep/ img {
    height: rem-calc(8);
    width: rem-calc(8);

    g {
      fill: #c5d0de;
    }
  }

  a:not(.back) {
    img {
      height: rem-calc(16);
      width: rem-calc(16);
    }
  }

  .back {
    img {
      height: rem-calc(12);
      padding-left: rem-calc(8);
      transform: rotate(180deg);
      width: rem-calc(12);
    }
  }

  @include breakpoint(small) {
    padding: 0 rem-calc(16);
  }

  .spacer {
    flex-grow: 1;
  }
}
</style>
